import { createContext, useContext } from "react";

export type Lang = "nl" | "en";

export const translations = {
  nav: {
    why: { nl: "Waarom", en: "Why" },
    who: { nl: "Wie we zijn", en: "Who we are" },
    what: { nl: "Wat we doen", en: "What we do" },
    refs: { nl: "Referenties", en: "References" },
    cta: { nl: "Neem contact op", en: "Get in touch" },
  },
  header: {
    sub: { nl: "Marketing die de techniek begrijpt.", en: "Marketing that understands the tech." },
    eyebrow: { nl: "Ellen Verhoeven · Founder A-WAVE", en: "Ellen Verhoeven · Founder A-WAVE" },
    headlineLine1: {
      nl: "Je hebt het beste product.",
      en: "You have the best product.",
    },
    headlineLine2: {
      nl: "Maar niemand weet het.",
      en: "But nobody knows it.",
    },
    sublead: {
      nl: "Marketingsystemen voor technische en industriële bedrijven. Geen vage campagnes. Wel meer zichtbaarheid, kwalitatieve leads en een website die verkoopt.",
      en: "Marketing systems for technical and industrial companies. No vague campaigns. More visibility, qualified leads and a website that sells.",
    },
    tagline: {
      nl: "Marketing die de techniek begrijpt.",
      en: "Marketing that understands the tech.",
    },
    ctaPrimary: { nl: "Laten we sparren", en: "Let's spar" },
    ctaSecondary: { nl: "Bekijk de aanpak", en: "See the approach" },
    stats: {
      nl: [
        { value: "11+", label: "Jaar marketing" },
        { value: "8+", label: "Merken gegroeid" },
        { value: "30+", label: "Jaar industrie-ervaring partner" },
      ],
      en: [
        { value: "11+", label: "Years in marketing" },
        { value: "8+", label: "Brands grown" },
        { value: "30+", label: "Years industry experience partner" },
      ],
    },
  },
  problem: {
    label: { nl: "01 — Herken je dit?", en: "01 — Sound familiar?" },
    h2: {
      nl: "Marketing die de techniek begrijpt.",
      en: "You have the best product. And still nobody finds you.",
    },
    p1: {
      nl: "Bedrijven in de techniek en de industrie verliezen geen deals op kwaliteit. Maar je verliest wel geïnteresseerde kopers door je online visitekaartje en positie niet goed te pakken. De koper googelt, zoekt in ChatGPT, vergelijkt en kiest. Vaak nog voordat hij je belt. Wie er dan niet staat, of met een site uit 2014 meedoet, verliest dit spel. Hoe goed je product ook is.",
      en: "Tech and industrial companies don't lose deals on quality. You lose interested buyers because your online presence isn't on point. The buyer Googles, asks ChatGPT, compares and decides — often before picking up the phone. Whoever isn't there, or shows up with a site from 2014, loses the game. No matter how good the product is.",
    },
    p2: {
      nl: "A-WAVE bouwt dat systeem voor je. Vindbaarheid, kwalitatieve leads en een website die wél verkoopt. Geen losse tactieken. Één werkend geheel.",
      en: "A-WAVE builds that system for you. Visibility, qualified leads and a website that actually sells. No isolated tactics. One working whole.",
    },
  },
  who: {
    label: { nl: "Wie we zijn", en: "Who we are" },
    h2: { nl: "Techniek + marketing. Geen compromis.", en: "Technical + Marketing. No compromise." },
    sub: {
      nl: "Twee werelden die elkaar nodig hebben, maar zelden samenkomen: technische expertise en marketing. A-WAVE maakt er één groeisysteem van.",
      en: "Two worlds that need each other, but rarely meet: technical expertise and marketing. A-WAVE turns them into one growth system.",
    },
    members: {
      nl: [
        {
          name: "Ellen Verhoeven",
          role: "Founder A-WAVE · Digital Growth",
          desc: "Founder van A-WAVE. Bouwt groeisystemen voor bedrijven in de service en industrie door Google Ads, SEO, leadgeneratie en website-optimalisatie te smeden tot één werkend geheel. Eerder verantwoordelijk voor de groei van merken als Kruidvat, VodafoneZiggo, Suzuki en Gasfedershop — van Google Ads Specialist tot Hoofd Marketing. Vertaalt data naar beslissingen en beslissingen naar leads en omzet.",
          tags: ["Google Ads", "SEO", "Lead Generation", "Analytics", "Website optimalisatie", "Mailfunnels", "AI Content Creator"],
        },
        {
          name: "Ron van de Weerd",
          role: "Technische Expertise & Netwerk",
          desc: "Oprichter van VCC Valves & Controls en ASE Repair. Voorzitter van Ondernemersvereniging Genootschap 24. 30+ jaar in valves, cryogene appendages en industriële systemen. De sparringpartner die DGA's bellen als ze vastlopen. Operationeel én strategisch. Hij kent jouw klant, markt en techniek van binnenuit.",
          tags: ["Valves", "Pumps", "Cryogeen", "Sparringpartner", "DGA-netwerk", "Genootschap 24"],
        },
      ],
      en: [
        {
          name: "Ellen Verhoeven",
          role: "Founder A-WAVE · Digital Growth",
          desc: "Founder of A-WAVE. Builds growth systems for technical and industrial companies by forging Google Ads, SEO, lead generation and website optimisation into one working engine. Previously drove growth for brands like Kruidvat, VodafoneZiggo, Suzuki and Gasfedershop — from Google Ads Specialist to Head of Marketing. Turns data into decisions and decisions into leads and revenue.",
          tags: ["Google Ads", "SEO", "Lead Generation", "Analytics", "Website optimization", "Mail funnels", "AI Content Creator"],
        },
        {
          name: "Ron van de Weerd",
          role: "Technical Expertise & Network",
          desc: "Founder of VCC Valves & Controls and ASE Repair. Chair of the entrepreneurs' association Genootschap 24. 30+ years in valves, cryogenic accessories and industrial systems. The sparring partner CEOs call when they are stuck. Operational and strategic. He knows your customer, market and technology from the inside.",
          tags: ["Valves", "Pumps", "Cryogenic", "Sparring Partner", "CEO Network", "Genootschap 24"],
        },
      ],
    },
  },
  services: {
    label: { nl: "03 — Het systeem", en: "03 — The system" },
    h2: { nl: "Audit. Bouwen. Groeien. Resultaat.", en: "Audit. Build. Grow. Report." },
    sub: {
      nl: "Geen losse tactieken. Eén online groeisysteem. Vier fases, vaste output, meetbaar resultaat.",
      en: "No isolated tactics. One online growth system. Four phases, fixed output, measurable result.",
    },
    items: {
      nl: [
        {
          name: "Audit",
          step: "Week 1–2",
          desc: "Vindbaarheid, website, ads en funnel onder de loep.",
          output: ["Scan huidige marketing performance", "Technisch SEO en AI SEO-rapport", "Leadgeneratie funnel", "3–5 prioriteiten"],
        },
        {
          name: "Bouwen",
          step: "Week 3–6",
          desc: "Het fundament: landingspagina's die leads leveren, Google Ads-setup, SEO-fundament en een website die werkt én bijgehouden wordt.",
          output: ["Herbouwde landingspagina's", "Google Ads-setup", "Lead-tracking", "Contentplan 6 maanden"],
        },
        {
          name: "Groeien",
          step: "Maand 2–6",
          desc: "Het systeem draait. Wekelijks bijsturen op Google Ads, SEO-content en conversie. Geen rapportage zonder actie.",
          output: ["SEO-artikelen per maand", "Ads-optimalisaties wekelijks op basis van what werkt", "A/B-tests op leads"],
        },
        {
          name: "resultaat",
          step: "Doorlopend",
          desc: "Eén dashboard. Eén sparring-call per maand. Jij weet altijd wat er gebeurt en wat het oplevert. Roadmap volgende maand.",
          output: ["Live dashboard", "Maandrapport (1 pagina)", "Sparring-call 60 min", "Roadmap volgende maand"],
        },
      ],
      en: [
        {
          name: "Audit",
          step: "Week 1–2",
          desc: "Findability, website, ads and funnel under the microscope.",
          output: ["Visibility scan", "Technical SEO report", "Funnel leak analysis", "3–5 priorities"],
        },
        {
          name: "Build",
          step: "Week 3–6",
          desc: "The foundation: landing pages that deliver leads, Google Ads setup, SEO fundamentals and a website that works and is maintained.",
          output: ["Rebuilt landing pages", "Google Ads setup", "Lead tracking", "6-month content plan"],
        },
        {
          name: "Grow",
          step: "Month 2–6",
          desc: "The system runs. Weekly course-corrections on Google Ads, SEO content and conversion. No reporting without action.",
          output: ["SEO articles per month", "Weekly ad optimisations based on what works", "A/B tests on leads"],
        },
        {
          name: "Report",
          step: "Ongoing",
          desc: "One dashboard. One sparring call per month. You always know what's happening and what it delivers.",
          output: ["Live dashboard", "1-page monthly report", "60-min sparring call", "Roadmap next month"],
        },
      ],
    },
    event: {
      title: { nl: "Eén systeem. Geen losse tactieken.", en: "One system. Not isolated tactics." },
      desc: {
        nl: "Marketing, vindbaarheid, website en leadflow werken samen. Daarom werkt het.",
        en: "Marketing, findability, website and lead flow work together. That's why it works.",
      },
    },
    addon: {
      tag: { nl: "Add-on · los inzetbaar", en: "Add-on · standalone" },
      name: { nl: "AI Content Creator", en: "AI Content Creator" },
      desc: {
        nl: "Jouw team produceert elke week content. Dat kost tijd en levert inconsistentie op zodra er iemand bij- of afvalt. Wij bouwen een AI-systeem op maat, gevoed met jouw tone of voice, merkrichtlijnen en kanaalformats. Iedereen in je team maakt on-brand content, in minuten in plaats van uren. Inclusief templates, beeldgenerators en teamtraining. Eenmalige investering. Kennis blijft in het bedrijf.",
        en: "Your team produces content every week. It takes time and the moment someone joins or leaves, consistency drops. We build a custom AI system, fed with your tone of voice, brand guidelines and channel formats. Everyone on your team makes on-brand content in minutes instead of hours. Includes templates, image generators and team training. One-time investment. Knowledge stays in the company.",
      },
    },
  },
  forWhom: {
    label: { nl: "03 — Voor wie", en: "03 — For whom" },
    h2: { nl: "Jij herkent dit.", en: "You recognize this." },
    items: {
      nl: [
        { strong: "Je hebt een sterk product", rest: ", maar je concurrenten zijn beter zichtbaar online, terwijl jij technisch veel verder bent." },
        { strong: "Je wil groeien,", rest: "maar krijgt marketing er niet professioneel bij gedaan naast je operationele rol." },
        { strong: "Je hebt al met een marketingbureau gewerkt,", rest: "maar zij begrepen de techniek niet, en dat zag je terug in de resultaten." },
        { strong: "Je organiseert events of beurzen,", rest: "maar de opvolging en online zichtbaarheid erna zou zoveel meer kunnen opleveren." },
      ],
      en: [
        { strong: "You have a strong product", rest: ", but your competitors are more visible online, even though you're technically far ahead." },
        { strong: "You want to grow,", rest: "but can't tackle marketing professionally alongside your operational role." },
        { strong: "You've worked with a marketing agency before,", rest: "but they didn't understand the technology, and you saw that in the results." },
        { strong: "You organize events and trade shows,", rest: "but the follow-up and online visibility afterwards could deliver so much more." },
      ],
    },
    closing: {
      nl: "Herken je er één van? Dan moeten we praten.",
      en: "Recognize even one? Let's talk.",
    },
  },
  partners: {
    label: { nl: "Samenwerking", en: "Partnership" },
    h2: { nl: "Partners & Netwerk", en: "Partners & Network" },
    sub: {
      nl: "Wij werken samen met een select netwerk van specialisten die de technische industrie kennen.",
      en: "We work with a select network of specialists who know the technical industry.",
    },
    items: {
      nl: [
        { name: "Power Networking & Events", role: "Larissa Ignacio", desc: "Producer van TEDx Eindhoven 2025 en internationale conferenties. Verbindt ondernemers, bestuurders en specialists wereldwijd. Waar handdrukken business worden." },
        { name: "FESMA", role: "Samuël Smit-Ockeloen", desc: "Business Process engineer & Automation" },
        { name: "SmartIQ", role: "IT Partner", desc: "Technologie, data en digitale infrastructuur." },
      ],
      en: [
        { name: "Power Networking & Events", role: "Larissa Ignacio", desc: "Producer of TEDx Eindhoven 2025 and international conferences. Connects entrepreneurs, executives and specialists worldwide. Where handshakes turn into business." },
        { name: "FESMA", role: "Samuël Smit-Ockeloen", desc: "Business Process engineer & Automation" },
        { name: "SmartIQ", role: "IT Partner", desc: "Technology, data and digital infrastructure." },
      ],
    },
  },
  refs: {
    label: { nl: "Referenties", en: "References" },
    h2: { nl: "Campagnes gedraaid voor", en: "Campaigns run for" },
    items: {
      nl: [
        { name: "gasfedershop.de", desc: "Technisch SEO, internationaal groeitraject" },
        { name: "Suzuki", desc: "Google Ads, leadgeneratie en dealercampagnes" },
        { name: "Kruidvat", desc: "Google Ads, omnichannel & Global Digital Excellence Award" },
        { name: "VodafoneZiggo", desc: "Google Ads (SEA), Media budget management, Omnichannel, AMMA Award" },
        { name: "VIV Support", desc: "E-commerce Management full-scope" },
        { name: "TEDx Eindhoven", desc: "Productie & curatie van het TEDx Eindhoven podium" },
        { name: "Eindhoven Entrepreneurs", desc: "Community & netwerkevents voor ondernemers in de regio" },
      ],
      en: [
        { name: "gasfedershop.de", desc: "Technical SEO, international growth trajectory" },
        { name: "Suzuki", desc: "Google Ads, lead generation and dealer campaigns" },
        { name: "Kruidvat", desc: "Google Ads, omnichannel & Global Digital Excellence Award" },
        { name: "VodafoneZiggo", desc: "Google Ads (SEA), Media budget management, Omnichannel, AMMA Award" },
        { name: "VIV Support", desc: "E-commerce Management full-scope" },
        { name: "TEDx Eindhoven", desc: "Production & curation of the TEDx Eindhoven stage" },
        { name: "Eindhoven Entrepreneurs", desc: "Community & networking events for regional entrepreneurs" },
      ],
    },
    knownLabel: { nl: "Bekend van", en: "Known from" },
    knownH2: { nl: "In de industrie", en: "In the industry" },
    knownSub: {
      nl: "Onze roots liggen diep in de service en industrie. We zijn geen buitenstaanders; we zijn er thuis.",
      en: "Our roots run deep in the technical sector. We're not outsiders; we're at home here.",
    },
    known: {
      nl: [
        { name: "VCC Valves & Controls", desc: "Opgericht door Ron van de Weerd. Specialist in industriële valves en controls." },
        { name: "ASE Repair", desc: "Mede opgericht door Ron. Reparatie en service van cryogene appendages en industriële systemen." },
      ],
      en: [
        { name: "VCC Valves & Controls", desc: "Founded by Ron van de Weerd. Specialist in industrial valves and controls." },
        { name: "ASE Repair", desc: "Co-founded by Ron. Repair and service of cryogenic accessories and industrial systems." },
      ],
    },
  },
  quote: {
    text: {
      nl: "Wij begrijpen je klant. We begrijpen je product. En wij weten hoe je groeit.",
      en: "We understand your customer. We understand your product. And we know how you grow.",
    },
    sub: { nl: "A-WAVE · Marketing & Events voor bedrijven in de service en industrie", en: "A-WAVE · Marketing & Events for companies in service and industry" },
  },
  closingCta: {
    h2: { nl: "In 20 minuten weet je of we een match zijn.", en: "20 minutes. Then you know if we are a match." },
    sub: {
      nl: "\n",
      en: "\n",
    },
    cta: { nl: "Laten we sparren", en: "Let's spar" },
    reassurance: {
      nl: "Reactie binnen 24 uur · 20 min intake · geen verkooppraat",
      en: "Response within 24 hours · 20-min intake · no sales pitch",
    },
  },
  founders: {
    label: { nl: "02 — A-WAVE", en: "02 — A-WAVE" },
    h2: {
      nl: "Ellen bouwt het marketing systeem. Ron heeft de technische vakkennis.",
      en: "Ellen builds the system. Ron brings the technical expertise.",
    },
    ellen: {
      number: { nl: "02", en: "02" },
      role: { nl: "Founder A-WAVE · Digital Growth", en: "Founder A-WAVE · Digital Growth" },
      kicker: { nl: "Ellen Verhoeven", en: "Ellen Verhoeven" },
      quote: {
        nl: "Er zijn veel bedrijven in de service en industrie met een geweldig product en hart voor de zaak, maar een website en marketing die grandioos achterloopt. Dát wilde ik oplossen.",
        en: "There are many companies in service and industry with a great product and heart for the work, but a website and marketing that's significantly lagging behind. That's what I wanted to fix.",
      },
      body: {
        nl: "Jarenlang bouwde ik groei voor merken als VodafoneZiggo en Suzuki. Grote budgetten, snelle resultaten. Maar mijn hart bleef toch bij de ondernemer die zelf op zaterdagavond nog een offerte tikt, en de machinebouwer met vijftig jaar vakmanschap en een website uit 2014. Daar valt écht het verschil te maken. Daarom startte ik A-WAVE. Ik werk met een team van specialisten dat ik flexibel inzet op wat jouw situatie vraagt. Geen vast bureau met vaste formats. Wel de juiste mensen op het juiste moment.",
        en: "For eleven years I built growth for VodafoneZiggo and Suzuki. Big budgets, fast results. But my heart is with the owner-CEO still writing quotes on a Saturday night, and the machine builder with fifty years of craftsmanship and a site from 2014. That's where the real wins are. So I started A-WAVE. I work with a team of specialists I deploy flexibly to fit your situation. No fixed agency with fixed formats. The right people at the right moment.",
      },
    },
    ron: {
      number: { nl: "02b", en: "02b" },
      role: { nl: "30+ jaar ervaring in service & industrie", en: "30+ years experience in service & industry" },
      kicker: { nl: "Ron van de Weerd", en: "Ron van de Weerd" },
      quote: {
        nl: "Elk woord op je website moet technisch kloppen. En de juiste DGA's bereik je via een netwerk dat je niet koopt.",
        en: "Every word on your site has to be technically right. And the right owner-CEOs are reached through a network you can't buy.",
      },
      body: {
        nl: "Ron brengt dertig jaar industrie-ervaring mee. Valves, cryogene systemen, industriële installaties. Oprichter van VCC Valves & Controls en ASE Repair. Hij zorgt dat we je business begrijpen, is sparringspartner voor de DGA en heeft een netwerk dat nog wel van pas kan komen voor je bedrijf.",
        en: "Ron brings thirty years of industry experience. Valves, cryogenic systems, industrial installations. Founder of VCC Valves & Controls and ASE Repair. He makes sure we understand your business, acts as sparring partner for the owner-CEO and brings a network that may well come in handy for your company.",
      },
    },
    together: {
      kicker: { nl: "Samen", en: "Together" },
      body: {
        nl: "Ellen bouwt het systeem. Ron brengt de techniek en het DGA-netwerk. Geen bureau dat probeert te begrijpen wat je doet — een partner die het al weet.",
        en: "Ellen builds the system. Ron brings the tech and the owner-CEO network. Not an agency trying to understand what you do — a partner who already knows.",
      },
    },
  },
  industries: {
    label: { nl: "04 — Sector", en: "04 — Sector" },
    h2: { nl: "Wij spreken jouw taal.", en: "We speak your language." },
    sub: {
      nl: "",
      en: "",
    },
    cta: { nl: "Stuur Ron een bericht over jouw machinebouw-vraagstuk", en: "Message Ron about your machine-building case" },
    reassurance: {
      nl: "Reactie binnen 24 uur · 20 min intake · geen verkooppraat",
      en: "Reply within 24 hours · 20-min intake · no sales pitch",
    },
    combined: {
      tagline: {
        nl: "Lange salescycli · technische kopers · hoge ordergrootte",
        en: "Long sales cycles · technical buyers · high-value orders",
      },
      pain: {
        nl: "Engineers vergelijken jou met 3 concurrenten op specs — maar je website laadt traag, mist datasheets en spreekt geen vakjargon. Resultaat: je valt af vóór je in gesprek komt.",
        en: "Engineers compare you with 3 competitors on specs — but your site loads slowly, lacks datasheets and doesn't speak the language. Result: you're cut before the conversation starts.",
      },
      approach: [
        {
          nl: "Productpagina's die op long-tail specs ranken (ATEX, IP-rating, materiaalkeuze).",
          en: "Product pages that rank on long-tail specs (ATEX, IP rating, material choice).",
        },
        {
          nl: "Lead magnets: technische whitepapers, 3D-configurators, ROI-calculators.",
          en: "Lead magnets: technical whitepapers, 3D configurators, ROI calculators.",
        },
        {
          nl: "ABM-campagnes op 20–50 named accounts in plaats van breed sproeien.",
          en: "ABM campaigns on 20–50 named accounts instead of broad spray-and-pray.",
        },
      ],
    },
  },

  footer: {
    location: { nl: "Millingen aan de Rijn, Nederland", en: "Millingen aan de Rijn, Netherlands" },
    rights: { nl: "Alle rechten voorbehouden.", en: "All rights reserved." },
  },
} as const;

export type Translations = typeof translations;

export interface I18nContextType {
  lang: Lang;
  setLang: (lang: Lang) => void;
  t: Translations;
}

export const I18nContext = createContext<I18nContextType | null>(null);

export const useI18n = () => {
  const ctx = useContext(I18nContext);
  if (!ctx) throw new Error("useI18n must be used within I18nProvider");
  return ctx;
};
